/**
 * Decoder for the Brazilian CIN (Carteira de Identidade Nacional) QR code,
 * shared via the gov.br app. Unlike the CNH-e binary format, this QR holds a
 * signed JWT (ES512) whose claims carry the document data; the rest lives
 * behind the `url` claim. The QR text is plain base64url, so the decoder's
 * `text` output already yields it — we just parse and label the claims.
 */

export interface CinData {
  /** JWT header algorithm, e.g. "ES512". */
  alg?: string;
  /** Decoded JWT claims (payload). */
  claims: Record<string, unknown>;
  /** True when issued by Brazil's MJSP (i.e. a genuine CIN). */
  isCin: boolean;
  /** Raw signature bytes as hex (for ES512 this is r‖s, 132 bytes). */
  signatureHex: string;
  /** Human-readable signature algorithm/curve. */
  signatureAlgorithm: string;
  /** The signing input (`header.payload`) needed to verify the signature. */
  signingInput: string;
}

function base64UrlToBytes(segment: string): Uint8Array {
  let s = segment.replace(/-/g, '+').replace(/_/g, '/');
  while (s.length % 4) {
    s += '=';
  }
  return Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
}

function base64UrlToJson(segment: string): Record<string, unknown> {
  const text = new TextDecoder().decode(base64UrlToBytes(segment));
  return JSON.parse(text) as Record<string, unknown>;
}

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

const ALG_LABELS: Record<string, string> = {
  ES512: 'ES512 (ECDSA P-521)',
  ES384: 'ES384 (ECDSA P-384)',
  ES256: 'ES256 (ECDSA P-256)',
  RS256: 'RS256 (RSA SHA-256)',
};

/**
 * Parses a CIN JWT from decoded QR text. Returns null if the text isn't a
 * JWT (three base64url segments with a JSON header), so ordinary QR codes
 * fall through to plain-text display.
 */
export function parseCinQr(text: string): CinData | null {
  const parts = text.split('.');
  if (parts.length !== 3) {
    return null;
  }
  try {
    const header = base64UrlToJson(parts[0]);
    const claims = base64UrlToJson(parts[1]);
    const alg = header['alg'];
    if (typeof alg !== 'string') {
      return null;
    }
    return {
      alg,
      claims,
      isCin: claims['iss'] === 'MJSP',
      signatureHex: toHex(base64UrlToBytes(parts[2])),
      signatureAlgorithm: ALG_LABELS[alg] ?? alg,
      signingInput: `${parts[0]}.${parts[1]}`,
    };
  } catch {
    return null;
  }
}

/** Formats an 11-digit CPF as 000.000.000-00; returns the input unchanged otherwise. */
function formatCpf(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 11) {
    return value;
  }
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

/** Human label + formatted value for a single CIN claim. */
export interface CinField {
  label: string;
  value: string;
}

const CLAIM_LABELS: Record<string, string> = {
  iss: 'Emissor',
  cpf: 'CPF',
  dns: 'Data de nascimento',
  dvd: 'Data de validade',
  url: 'Validação',
};

/** Turns the raw claims into ordered, labelled rows for display. */
export function cinFields(data: CinData): CinField[] {
  const order = ['iss', 'cpf', 'dns', 'dvd', 'url'];
  const known = new Set(order);
  const rows: CinField[] = [];

  for (const key of order) {
    const raw = data.claims[key];
    if (raw == null || raw === '') {
      continue;
    }
    const value = key === 'cpf' ? formatCpf(String(raw)) : String(raw);
    rows.push({ label: CLAIM_LABELS[key], value });
  }
  // Surface any extra claims we don't have a label for.
  for (const [key, raw] of Object.entries(data.claims)) {
    if (!known.has(key) && raw != null && raw !== '') {
      rows.push({ label: key, value: String(raw) });
    }
  }
  return rows;
}
